import { useEffect, useState } from 'react'
import { CopyIcon, LoaderCircleIcon, PlusIcon, RotateCwIcon, ShoppingCartIcon } from 'lucide-react'
import { LinkBase, useGetList, useGetOne, useNotify, useRefresh } from 'ra-core'
import { Confirm } from '@internal/ui-kit/components/admin/confirm'
import { Alert, AlertDescription, AlertTitle } from '@internal/ui-kit/components/ui/alert'
import { Button, buttonVariants } from '@internal/ui-kit/components/ui/button'
import { Combobox } from '@internal/ui-kit/components/ui/combobox'
import { Input } from '@internal/ui-kit/components/ui/input'
import { Label } from '@internal/ui-kit/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@internal/ui-kit/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@internal/ui-kit/components/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@internal/ui-kit/components/ui/table'
import { extractErrorMessage, httpClient } from '@internal/ui-kit/lib/httpClient'
import { AddCartItemPanel } from './AddCartItemPanel'
import type { DeliveryAddress } from './useCart'
import { useCart, useDeliveryAddressesByUser, useDeliveryMethodsByBrand } from './useCart'

type UserRecord = {
  id: string | number
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  phone_number?: string | null
  date_of_birth?: string | null
  // The real user record's own field name for what the booking-details
  // form calls `gender` (ticket feedback: "gender is sex in user").
  sex?: string | null
  scopes?: string[] | null
}

/** Same `label`/`scopes` shape `apps/audit-log/index.tsx`'s own user picker already established, so an admin scope reads the same way in both dropdowns (ticket feedback: "dropdown doesn't show me scopes"). */
function userLabel(user: UserRecord): string {
  const name = user.first_name ? `${user.first_name} ${user.last_name ?? ''}`.trim() : undefined
  const label = name ? `${name} (${user.email ?? '—'})` : (user.email ?? String(user.id))
  return user.scopes?.length ? `${label} — ${user.scopes.join(', ')}` : label
}

type CartItem = { order_item_id: string; product_snapshot?: unknown; quantity?: number | null; price?: number | null; final_price?: number | null }

/** `item.product_snapshot.title` (ticket feedback: "need to show product title in list of items"), same nested field `OrderItemsSection.tsx`'s `orderItemProductTitle` already reads off a placed order's own items. */
function cartItemProductTitle(item: CartItem): string {
  const snapshot = item.product_snapshot as { title?: string } | null | undefined
  return snapshot?.title ?? '—'
}

/** Same "copy to clipboard" affordance `apps/audit-log/index.tsx`/`apps/orders/index.tsx` already use next to a raw id an admin shouldn't need to see (ticket feedback: "i don't need to see order_item_id, so you can show small button copy order_item_id instead"). */
function CopyIconButton({ value, label }: { value: string; label: string }) {
  const notify = useNotify()
  const handleCopy = async () => {
    await navigator.clipboard.writeText(value)
    notify(`${label} copied`, { type: 'info' })
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-muted-foreground hover:text-foreground shrink-0"
      title={`Copy ${label.toLowerCase()}`}
    >
      <CopyIcon className="size-3.5" />
    </button>
  )
}

/** Debounces the user search input, matching the source app's own 750ms `queryThrottleTime` on `getUsers` (`apps/seo-manager/index.tsx`'s `useDebouncedValue` precedent). */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

/**
 * The admin-assisted cart-builder/checkout workflow (HATCH-149 — see
 * MIGRATION-NOTES.md's "Spec" for the full design). Entry point for
 * `OrdersPage`'s "Manage user cart" toolbar button, and (HATCH-266) for a
 * user's own profile page — see `fixedUserId` below.
 *
 * `fixedUserId` (ticket feedback: "parametrize the component with user_id -
 * if it's set - user pre-selected - and you can't change it, so that we can
 * use exactly same component") pre-selects that user and locks the picker,
 * so `UserProfilePage`'s own "Manage user cart" button can reuse this exact
 * component instead of a separate one.
 */
export function ManageUserCartDialog({ fixedUserId }: { fixedUserId?: string } = {}) {
  const notify = useNotify()
  const refresh = useRefresh()
  const [open, setOpen] = useState(false)
  const [userSearch, setUserSearch] = useState('')
  const debouncedUserSearch = useDebouncedValue(userSearch, 750)
  const [isAddingItem, setIsAddingItem] = useState(false)
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null)
  const [discountCode, setDiscountCode] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [email, setEmail] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  // The admin-entered delivery address/methods (HATCH-519) — cart-level, not
  // per-item (the ticket's own refined spec: `delivery_address` sits on the
  // order/cart as a whole, matching `orderSchema.delivery_address` being one
  // object on a placed order, not on each item). `deliveryAddressId` is set
  // locally once `saveDeliveryAddress` both creates the address record and
  // attaches it to the order (`PATCH admin/orders/{order_id}/delivery-address`)
  // — that second call is required before `POST .../current/delivery-methods`
  // will accept anything (it 400s "Delivery Address has to be specified
  // first" otherwise, confirmed live against this exact flow).
  const [deliveryRegionCode, setDeliveryRegionCode] = useState('')
  const [deliveryCityCode, setDeliveryCityCode] = useState('')
  const [deliveryBarangay, setDeliveryBarangay] = useState('')
  const [deliveryStreet, setDeliveryStreet] = useState('')
  const [deliveryFloorOrUnitNo, setDeliveryFloorOrUnitNo] = useState('')
  const [deliveryZipCode, setDeliveryZipCode] = useState('')
  const [deliveryAddressId, setDeliveryAddressId] = useState('')
  // Whether the create-new-address form is showing, as opposed to the
  // pick-an-existing-address list — only relevant while `!deliveryAddressId`.
  // A user with no addresses on file yet skips straight to the form (see
  // render below), so this only ever matters once at least one exists.
  const [isAddingNewAddress, setIsAddingNewAddress] = useState(false)
  // One chosen delivery method per brand represented in the cart's
  // qualifying items, keyed by `brand_id` — `POST .../current/delivery-methods`
  // (`UpdateDeliveryMethods`, confirmed via `oaq.py`) takes exactly this shape.
  const [selectedDeliveryMethodIds, setSelectedDeliveryMethodIds] = useState<Record<string, string>>({})
  // The picked user's own record, not just their id — ticket feedback:
  // "when i selected somebody - input shows user_id". Looking the id back up
  // against `users` (the current search-result page) broke once the search
  // that produced it was cleared/changed, since that page no longer includes
  // the selected user; keeping the record itself instead means the
  // `Combobox`'s "selected" match (by value) always has this one option
  // available regardless of what's currently searched.
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null)
  const userId = fixedUserId ?? (selectedUser ? String(selectedUser.id) : '')

  const { data: fixedUser } = useGetOne<UserRecord>('users', { id: fixedUserId ?? '' }, { enabled: Boolean(fixedUserId) })

  const { data: users } = useGetList<UserRecord>(
    'users',
    { pagination: { page: 1, perPage: 25 }, sort: { field: 'first_name', order: 'ASC' }, filter: { q: debouncedUserSearch } },
    { enabled: !fixedUserId && open && debouncedUserSearch.length > 0 },
  )
  // The already-selected user's own option, kept available even once the
  // search that surfaced it no longer does — see `selectedUser`'s comment.
  const userOptions = [
    ...(selectedUser && !(users ?? []).some((user) => String(user.id) === userId)
      ? [{ value: userId, label: userLabel(selectedUser) }]
      : []),
    ...(users ?? []).map((user) => ({ value: String(user.id), label: userLabel(user) })),
  ]
  const { cart, isFetching: isCartFetching, refetch: refetchCart } = useCart(userId || undefined)

  const items = cart?.items ?? []
  // A physical product or a home-service booking (HATCH-519 acceptance
  // criteria) — `is_physical_product`/`schedule.location_type` are both
  // already on the cart item response (confirmed via `oaq.py`), so this is
  // derived straight from `items`, not tracked separately.
  const qualifyingItems = items.filter(
    (item) => item.is_physical_product || item.schedule?.location_type === 'home_service',
  )
  const needsDelivery = qualifyingItems.length > 0
  const brandNameById = new Map<string, string>()
  // Ticket feedback: "if order item already has delivery method, we have to
  // show it by default selected" — `item.delivery_method_id` is the
  // server-confirmed choice from an earlier `saveDeliveryMethods` call (or a
  // resumed cart); read here so the picker below can default to it instead
  // of always starting blank.
  const existingDeliveryMethodIdByBrand = new Map<string, string>()
  for (const item of qualifyingItems) {
    if (item.brand?.brand_id) brandNameById.set(item.brand.brand_id, item.brand.name ?? item.brand.brand_id)
    if (item.brand?.brand_id && item.delivery_method_id) {
      existingDeliveryMethodIdByBrand.set(item.brand.brand_id, item.delivery_method_id)
    }
  }
  const qualifyingBrandIds = [...brandNameById.keys()]
  const effectiveDeliveryMethodId = (brandId: string) =>
    selectedDeliveryMethodIds[brandId] ?? existingDeliveryMethodIdByBrand.get(brandId) ?? ''

  const { data: regions } = useGetList<{ id: string; name: string }>(
    'address-directory',
    { pagination: { page: 1, perPage: 1000 }, sort: { field: 'name', order: 'ASC' }, filter: {} },
    { enabled: needsDelivery && !deliveryAddressId },
  )
  const { data: cities } = useGetList<{ id: string; name: string }>(
    'address-directory-cities',
    { pagination: { page: 1, perPage: 1000 }, sort: { field: 'name', order: 'ASC' }, filter: { region_code: deliveryRegionCode } },
    { enabled: needsDelivery && !deliveryAddressId && Boolean(deliveryRegionCode) },
  )
  // `cart.delivery_address.region_code`/`city_code` (server truth) over the
  // local `deliveryRegionCode`/`deliveryCityCode` state — that local state
  // is only ever populated while the create-address form itself is filled
  // in; picking an *existing* address (`applyExistingDeliveryAddress`) or
  // resuming a cart that already had one attached (the load-sync effect
  // below) never touches it, which otherwise left this query permanently
  // disabled (empty region/city) even once `deliveryAddressId` was set —
  // ticket feedback: "delivery method choice is broken... dropdown is
  // empty... even though I do expect compatible delivery methods for the
  // address I used."
  const deliveryMethodsRegionCode = cart?.delivery_address?.region_code ?? deliveryRegionCode
  const deliveryMethodsCityCode = cart?.delivery_address?.city_code ?? deliveryCityCode
  const { deliveryMethodsByBrand } = useDeliveryMethodsByBrand(
    deliveryAddressId ? qualifyingBrandIds : [],
    deliveryAddressId ? deliveryMethodsRegionCode : undefined,
    deliveryAddressId ? deliveryMethodsCityCode : undefined,
  )
  // Ticket feedback: "change address should show list of existing addresses
  // with ability to choose... and then there should be button add new
  // there" — fetched whenever the picker/create-form is showing at all, not
  // just on first load, since "Change address" re-shows this same picker.
  const { deliveryAddresses: existingDeliveryAddresses } = useDeliveryAddressesByUser(
    needsDelivery && !deliveryAddressId ? userId : undefined,
  )

  const selectUser = (user: UserRecord) => {
    setSelectedUser(user)
    setIsAddingItem(false)
    setPhoneNumber(user.phone_number ?? '')
    setEmail(user.email ?? '')
  }

  // The `fixedUserId` case pre-selects that user as soon as its record
  // arrives — there's no combobox `onChange` to drive `selectUser` off of —
  // and re-selects it every time the panel re-opens, since `reset()` (below)
  // clears the phone/email fields on close. Also covers the "cart might be
  // changed" / "if we open panel - we should fetch fresh cart if user
  // selected" feedback: react-query's own cache would otherwise show a
  // stale cart from an earlier open.
  useEffect(() => {
    if (!open) return
    if (fixedUserId && fixedUser) selectUser(fixedUser)
    if (userId) refetchCart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fixedUserId, fixedUser])

  // Ticket feedback: opening a cart that already has a `delivery_address_id`
  // (e.g. resuming one from an earlier session) forced the admin to enter a
  // brand-new address anyway — `deliveryAddressId` only ever got set as a
  // side effect of `saveDeliveryAddress` succeeding, never from the cart
  // itself. This picks up whatever the order already has, once per loaded
  // cart (keyed on `order_id`, not on `delivery_address_id` itself — so this
  // doesn't refire and clobber local state right after `saveDeliveryAddress`
  // intentionally clears it to let the admin replace the address).
  useEffect(() => {
    if (cart?.order_id && cart.delivery_address_id) setDeliveryAddressId(cart.delivery_address_id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.order_id])

  const reset = () => {
    setUserSearch('')
    if (!fixedUserId) setSelectedUser(null)
    setIsAddingItem(false)
    setDeleteItemId(null)
    setDiscountCode('')
    setPhoneNumber('')
    setEmail('')
    setDeliveryRegionCode('')
    setDeliveryCityCode('')
    setDeliveryBarangay('')
    setDeliveryStreet('')
    setDeliveryFloorOrUnitNo('')
    setDeliveryZipCode('')
    setDeliveryAddressId('')
    setIsAddingNewAddress(false)
    setSelectedDeliveryMethodIds({})
  }

  const deleteItem = async () => {
    if (!deleteItemId) return
    setIsSaving(true)
    try {
      await httpClient(`/admin/orders/by-user/${userId}/current/item/${deleteItemId}`, { method: 'DELETE' })
      setDeleteItemId(null)
      refetchCart()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not remove this item.'), { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const applyDiscountCode = async () => {
    if (!discountCode) return
    setIsSaving(true)
    try {
      await httpClient(`/admin/orders/by-user/${userId}/current/discount_code`, {
        method: 'PUT',
        body: JSON.stringify({ discount_code: discountCode }),
      })
      setDiscountCode('')
      refetchCart()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not apply this discount code.'), { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const removeDiscountCode = async () => {
    setIsSaving(true)
    try {
      await httpClient(`/admin/orders/by-user/${userId}/current/discount_code`, { method: 'DELETE' })
      refetchCart()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not remove this discount code.'), { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const selectedRegion = (regions ?? []).find((region) => region.id === deliveryRegionCode)
  const selectedCity = (cities ?? []).find((city) => city.id === deliveryCityCode)
  const canSaveDeliveryAddress = Boolean(
    selectedRegion && selectedCity && deliveryBarangay.trim() && deliveryStreet.trim() && deliveryZipCode.trim(),
  )
  // `cart.delivery_address` (server truth) once it's caught up with a
  // `saveDeliveryAddress` call or a resumed cart already had one — the local
  // region/city/barangay/etc. fields are only ever populated while the
  // create-address form itself is open, so they can't be relied on to
  // display an address that was set in an earlier session/dialog-open.
  const savedAddress =
    cart?.delivery_address ??
    (selectedRegion && selectedCity
      ? {
          region: selectedRegion.name,
          city: selectedCity.name,
          barangay: deliveryBarangay,
          street: deliveryStreet,
          floor_or_unit_no: deliveryFloorOrUnitNo,
          zip_code: deliveryZipCode,
        }
      : null)

  /**
   * `POST delivery-addresses/` (HATCH-519, `CreateDeliveryAddress` confirmed
   * via `oaq.py`) — `owner_id` is an explicit body field, so an admin can
   * create this for any user, not just their own account. Then
   * `PATCH admin/orders/{order_id}/delivery-address` (`UpdateDeliveryAddressRequest`,
   * added after this ticket's own review round surfaced the gap: there was
   * no admin-scoped way to attach an address to an in-progress order before
   * checkout, so `POST .../current/delivery-methods` 400'd with "Delivery
   * Address has to be specified first" even after the address itself
   * existed) — this is the actual attach-to-order step; without it, the
   * address record exists but the order's own `delivery_address_id` stays
   * unset.
   */
  /** `PATCH admin/orders/{order_id}/delivery-address` (`UpdateDeliveryAddressRequest`, confirmed via `oaq.py`) — the actual attach-to-order step, shared by both "create a new address" and "pick an existing one" below. */
  const attachDeliveryAddress = async (addressId: string) => {
    if (!cart?.order_id) return false
    await httpClient(`/admin/orders/${cart.order_id}/delivery-address`, {
      method: 'PATCH',
      body: JSON.stringify({ delivery_address_id: addressId }),
    })
    setDeliveryAddressId(addressId)
    refetchCart()
    return true
  }

  const saveDeliveryAddress = async () => {
    if (!canSaveDeliveryAddress || !selectedRegion || !selectedCity || !cart?.order_id) return
    setIsSaving(true)
    try {
      const address = await httpClient<{ delivery_address_id: string }>('/delivery-addresses/', {
        method: 'POST',
        body: JSON.stringify({
          owner_id: userId,
          region: selectedRegion.name,
          region_code: selectedRegion.id,
          city: selectedCity.name,
          city_code: selectedCity.id,
          barangay: deliveryBarangay,
          street: deliveryStreet,
          floor_or_unit_no: deliveryFloorOrUnitNo || undefined,
          zip_code: deliveryZipCode,
        }),
      })
      await attachDeliveryAddress(address.delivery_address_id)
      notify('Delivery address saved.', { type: 'info' })
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not save this delivery address.'), { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  /** Ticket feedback: "change address should show list of existing addresses with ability to choose". Skips `POST delivery-addresses/` entirely — the address already exists, only the order needs to point at it. */
  const applyExistingDeliveryAddress = async (address: DeliveryAddress) => {
    setIsSaving(true)
    try {
      await attachDeliveryAddress(address.delivery_address_id)
      notify('Delivery address set.', { type: 'info' })
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not set this delivery address.'), { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const canSaveDeliveryMethods = qualifyingBrandIds.length > 0 && qualifyingBrandIds.every((brandId) => effectiveDeliveryMethodId(brandId))

  /** `POST .../current/delivery-methods` (HATCH-519, `UpdateDeliveryMethods` confirmed via `oaq.py`) — one delivery method per brand represented in the cart; the backend applies each to that brand's own qualifying item(s), reflected back on `item.delivery_method_id` once the cart is refetched. */
  const saveDeliveryMethods = async () => {
    if (!canSaveDeliveryMethods) return
    setIsSaving(true)
    try {
      await httpClient(`/admin/orders/by-user/${userId}/current/delivery-methods`, {
        method: 'POST',
        body: JSON.stringify({
          delivery_methods: qualifyingBrandIds.map((brandId) => ({
            brand_id: brandId,
            delivery_method_id: effectiveDeliveryMethodId(brandId),
          })),
        }),
      })
      refetchCart()
      notify('Delivery methods saved.', { type: 'info' })
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not save delivery methods.'), { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  // Acceptance criteria #2: a delivery method must be picked (and, per #1, a
  // valid address entered) for every qualifying item before the cart can
  // proceed to checkout — `item.delivery_method_id` is the source of truth
  // for "already picked" (set server-side once `saveDeliveryMethods`
  // succeeds and the cart is refetched), not local-only picker state, so a
  // page refresh/reopen can't silently re-show this as unsatisfied.
  const deliveryRequirementSatisfied =
    !needsDelivery || (Boolean(deliveryAddressId) && qualifyingItems.every((item) => Boolean(item.delivery_method_id)))

  // A discount code or an inventory-only cart can bring `total_price` to
  // zero (ticket: "Free orders should not send link") — matching the
  // source export's own `button2` label swap (`parseFloat(
  // getSelectedUserOrder.data.total_price)`, `orders/components.json`),
  // which is the only place the source distinguishes a free cart from a
  // paid one. The live API sends `total_price` as a decimal string (e.g.
  // `"0.00"`), not a number (ticket feedback: "Total price is a decimal
  // and comes from backend as string") — a falsy/truthy check on the raw
  // value is always truthy for a non-empty string, so this needs the same
  // `parseFloat` the source export itself uses.
  const isFreeCart = !parseFloat(String(cart?.total_price ?? 0))

  const markForPaymentAndSendLink = async () => {
    setIsSaving(true)
    try {
      const result = await httpClient<{ paid_at?: string | null }>(
        `/admin/orders/by-user/${userId}/current/for_payment`,
        {
          method: 'POST',
          body: JSON.stringify({
            first_name: selectedUser?.first_name,
            last_name: selectedUser?.last_name,
            phone_number: phoneNumber,
            method: isFreeCart ? 'free' : 'xendit',
            email,
            // Already attached to the order by `saveDeliveryAddress`
            // (`PATCH admin/orders/{order_id}/delivery-address`) — repeating
            // it on `PaymentRequest` too is a harmless no-op confirmation,
            // not the only place it gets set.
            ...(needsDelivery ? { delivery_address_id: deliveryAddressId } : {}),
          }),
        },
      )
      if (!isFreeCart && !result?.paid_at) {
        await httpClient(`/admin/orders/by-user/${userId}/current/pay/xendit/invoice-link`, { method: 'POST' })
      }
      notify(isFreeCart ? 'Marked as paid.' : 'Marked for payment.', { type: 'info' })
      setOpen(false)
      reset()
      refresh()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not mark this cart for payment.'), { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const copyLink = async () => {
    if (!cart?.reference_url) return
    await navigator.clipboard.writeText(cart.reference_url)
    notify('Link copied.', { type: 'info' })
  }

  // Ticket feedback: "selected user must have email/phone number, first/last
  // name set, before we allow to manage user's order (those fields required
  // to process order). If not set - lets show notice and link to user
  // profile." `selectedUser` covers both the picker path and the
  // `fixedUserId` path (the effect above always feeds `fixedUser` through
  // `selectUser`), so this is the one place to check either way. `.trim()`
  // treats a whitespace-only value the same as missing — round 2 of this
  // feedback: "it still allows me to select products" on a user reported as
  // missing the required fields, so a blank-but-technically-non-null value
  // is now guarded against too, not just `null`/`undefined`/`''`.
  const isBlank = (value: string | null | undefined) => !value || !value.trim()
  const missingRequiredFields = selectedUser
    ? (
        [
          isBlank(selectedUser.first_name) && 'first name',
          isBlank(selectedUser.last_name) && 'last name',
          isBlank(selectedUser.email) && 'email',
          isBlank(selectedUser.phone_number) && 'phone number',
        ].filter(Boolean) as string[]
      )
    : []
  const canManageOrder = Boolean(selectedUser) && missingRequiredFields.length === 0

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        <ShoppingCartIcon />
        Manage user cart
      </Button>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) reset()
        }}
      >
        <SheetContent className="w-full sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Manage user cart</SheetTitle>
            <SheetDescription>Build a cart for a user and check them out.</SheetDescription>
          </SheetHeader>
          {/*
            `flex-1 min-h-0` (missing before, per `apps/products/MIGRATION-
            NOTES.md`'s own note on this exact `Sheet`) — without it this
            div has no bounded height inside the fixed-height `SheetContent`,
            so `overflow-y-auto` never actually triggers: the div just grows
            with its own content instead of scrolling within the space left
            between header and footer. Went from a latent bug to a reported
            one once the delivery address/method section (HATCH-519) added
            enough height to push the cart items table out of view below the
            fold, with the footer's buttons still pinned at the bottom
            (ticket feedback: "it's hard to see items after delivery address
            selected... too much stuff fixed to the bottom").
          */}
          <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto px-4">
            {fixedUserId ? (
              <div className="flex flex-col gap-1.5">
                <Label>User</Label>
                <p className="text-sm">{selectedUser ? userLabel(selectedUser) : fixedUserId}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cart-user">User</Label>
                <Combobox
                  id="cart-user"
                  options={userOptions}
                  value={userId}
                  onChange={(value) => {
                    const user = (users ?? []).find((candidate) => String(candidate.id) === value) ?? selectedUser
                    if (user) selectUser(user)
                  }}
                  placeholder="Search by name or email"
                  searchValue={userSearch}
                  onSearchChange={setUserSearch}
                  searchPlaceholder="Type to search…"
                  emptyText={debouncedUserSearch ? 'No matching user found.' : 'Start typing name or email.'}
                  className="w-full"
                />
              </div>
            )}

            {userId && selectedUser && !canManageOrder ? (
              <Alert variant="destructive">
                <AlertTitle>Missing required contact info</AlertTitle>
                <AlertDescription>
                  <p>This user is missing {missingRequiredFields.join(', ')} — required to process an order.</p>
                  <LinkBase to={`/users/${userId}/profile`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                    Go to user profile
                  </LinkBase>
                </AlertDescription>
              </Alert>
            ) : null}

            {userId && canManageOrder ? (
              <>
                {/*
                  `sticky top-0` (ticket feedback, after the `flex-1 min-h-0`
                  scroll fix: "I barely can see items — because everything
                  covered with frozen to bottom things") — the scroll region
                  itself was already bounded and scrolling correctly; the
                  actual complaint is that the cart items — the thing an
                  admin needs to keep checking against while filling in
                  delivery address/method/contact fields below — sat at the
                  very top of a long scroll and disappeared the moment any of
                  that got filled in. Pinning this block (items + totals) to
                  the top of the one scroll region keeps it visible
                  throughout without a second, nested scrollbar (ticket
                  feedback, round 2: "why do we need two scrolls?" — an
                  earlier pass also capped the table's own height with its
                  own `overflow-y-auto`; dropped in favor of this single
                  scroll everything else already uses).
                */}
                <div className="bg-background sticky top-0 z-10 flex flex-col gap-4 pb-2">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">Cart items</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={isCartFetching}
                      onClick={() => refetchCart()}
                      aria-label="Refresh cart"
                    >
                      {isCartFetching ? <LoaderCircleIcon className="animate-spin" /> : <RotateCwIcon />}
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead>Quantity</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Final price</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.order_item_id}>
                            <TableCell>
                              <span className="inline-flex items-center gap-1.5">
                                {cartItemProductTitle(item)}
                                <CopyIconButton value={item.order_item_id} label="Order item ID" />
                              </span>
                            </TableCell>
                            <TableCell>{item.quantity ?? '—'}</TableCell>
                            <TableCell>{item.price ?? '—'}</TableCell>
                            <TableCell>{item.final_price ?? '—'}</TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeleteItemId(item.order_item_id)}
                              >
                                Delete
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {items.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-muted-foreground">
                              No items in this cart yet.
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </div>

                  {/*
                    Ticket feedback, round 2 (correcting round 1's field
                    semantics): "final_price (this is price of items),
                    delivery fee, total_price ← this is total price customer
                    will pay with applied discounts, etc." plus "make total
                    total_amount right most - AND HIGHLIGHTED" — `total_price`
                    is the actual payable amount, not `final_price`; it now
                    trails the row and is the only one visually emphasized.
                  */}
                  <div className="flex justify-end gap-4 text-sm">
                    <span className="text-muted-foreground">Final price: {cart?.final_price ?? '—'}</span>
                    <span className="text-muted-foreground">Delivery fee: {cart?.delivery_fee ?? '—'}</span>
                    <span className="text-foreground font-semibold">Total price: {cart?.total_price ?? '—'}</span>
                  </div>
                </div>

                {needsDelivery ? (
                  <div className="flex flex-col gap-3 rounded-md border p-4">
                    <Label>Delivery address</Label>
                    {!deliveryAddressId && existingDeliveryAddresses.length > 0 && !isAddingNewAddress ? (
                      <div className="flex flex-col gap-2">
                        {existingDeliveryAddresses.map((address) => (
                          <div
                            key={address.delivery_address_id}
                            className="flex items-center justify-between gap-2 rounded-md border p-2"
                          >
                            <p className="text-sm">
                              {[
                                address.street,
                                address.floor_or_unit_no,
                                address.barangay,
                                address.city,
                                address.region,
                                address.zip_code,
                              ]
                                .filter(Boolean)
                                .join(', ')}
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={isSaving}
                              onClick={() => applyExistingDeliveryAddress(address)}
                            >
                              Use this address
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsAddingNewAddress(true)}
                        >
                          <PlusIcon />
                          Add new address
                        </Button>
                      </div>
                    ) : null}
                    {!deliveryAddressId && (existingDeliveryAddresses.length === 0 || isAddingNewAddress) ? (
                      <>
                        {existingDeliveryAddresses.length > 0 ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="self-start"
                            onClick={() => setIsAddingNewAddress(false)}
                          >
                            Back to existing addresses
                          </Button>
                        ) : null}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor="delivery-region">Region</Label>
                            <Select
                              value={deliveryRegionCode}
                              onValueChange={(value) => {
                                setDeliveryRegionCode(value)
                                setDeliveryCityCode('')
                              }}
                            >
                              <SelectTrigger id="delivery-region">
                                <SelectValue placeholder="Choose a region" />
                              </SelectTrigger>
                              <SelectContent>
                                {(regions ?? []).map((region) => (
                                  <SelectItem key={region.id} value={region.id}>
                                    {region.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor="delivery-city">City</Label>
                            <Select value={deliveryCityCode} onValueChange={setDeliveryCityCode} disabled={!deliveryRegionCode}>
                              <SelectTrigger id="delivery-city">
                                <SelectValue placeholder="Choose a city" />
                              </SelectTrigger>
                              <SelectContent>
                                {(cities ?? []).map((city) => (
                                  <SelectItem key={city.id} value={city.id}>
                                    {city.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor="delivery-barangay">Barangay</Label>
                            <Input
                              id="delivery-barangay"
                              value={deliveryBarangay}
                              onChange={(event) => setDeliveryBarangay(event.target.value)}
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor="delivery-zip">Zip code</Label>
                            <Input
                              id="delivery-zip"
                              value={deliveryZipCode}
                              onChange={(event) => setDeliveryZipCode(event.target.value)}
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor="delivery-street">Street address</Label>
                            <Input
                              id="delivery-street"
                              value={deliveryStreet}
                              onChange={(event) => setDeliveryStreet(event.target.value)}
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor="delivery-floor">Floor / Unit no.</Label>
                            <Input
                              id="delivery-floor"
                              value={deliveryFloorOrUnitNo}
                              onChange={(event) => setDeliveryFloorOrUnitNo(event.target.value)}
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={!canSaveDeliveryAddress || isSaving}
                          onClick={saveDeliveryAddress}
                        >
                          Save address
                        </Button>
                      </>
                    ) : null}
                    {deliveryAddressId ? (
                      <div className="flex flex-col gap-3">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-muted-foreground text-sm">
                            {[
                              savedAddress?.street,
                              savedAddress?.floor_or_unit_no,
                              savedAddress?.barangay,
                              savedAddress?.city,
                              savedAddress?.region,
                              savedAddress?.zip_code,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeliveryAddressId('')
                              setIsAddingNewAddress(false)
                              setDeliveryRegionCode('')
                              setDeliveryCityCode('')
                              setDeliveryBarangay('')
                              setDeliveryStreet('')
                              setDeliveryFloorOrUnitNo('')
                              setDeliveryZipCode('')
                              // A different region/city can offer a different
                              // set of delivery methods — any already-picked
                              // ones stop being valid choices once the
                              // address (and so the region/city they were
                              // scoped to) changes.
                              setSelectedDeliveryMethodIds({})
                            }}
                          >
                            Change address
                          </Button>
                        </div>
                        {qualifyingBrandIds.map((brandId) => (
                          <div key={brandId} className="flex flex-col gap-1.5">
                            <Label htmlFor={`delivery-method-${brandId}`}>Delivery method — {brandNameById.get(brandId)}</Label>
                            <Select
                              value={effectiveDeliveryMethodId(brandId)}
                              onValueChange={(value) => setSelectedDeliveryMethodIds((prev) => ({ ...prev, [brandId]: value }))}
                            >
                              <SelectTrigger id={`delivery-method-${brandId}`}>
                                <SelectValue placeholder="Choose a delivery method" />
                              </SelectTrigger>
                              <SelectContent>
                                {(deliveryMethodsByBrand[brandId] ?? []).map((method) => (
                                  <SelectItem key={method.delivery_method_id} value={method.delivery_method_id}>
                                    {method.label} {method.fee_amount != null ? `(${method.fee_amount})` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={!canSaveDeliveryMethods || isSaving}
                          onClick={saveDeliveryMethods}
                        >
                          Save delivery method{qualifyingBrandIds.length > 1 ? 's' : ''}
                        </Button>
                        {deliveryRequirementSatisfied ? (
                          <p className="text-muted-foreground text-sm">Delivery methods saved — ready to check out.</p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {isAddingItem ? (
                  <AddCartItemPanel
                    userId={userId}
                    bookingDetailsDefaults={{
                      first_name: selectedUser?.first_name ?? '',
                      last_name: selectedUser?.last_name ?? '',
                      email: selectedUser?.email ?? '',
                      phone_number: selectedUser?.phone_number ?? '',
                      date_of_birth: selectedUser?.date_of_birth ?? '',
                      // The booking-details form's own `gender` field —
                      // ticket feedback: the user record's equivalent field
                      // is named `sex`, not `gender`.
                      gender: selectedUser?.sex ?? '',
                    }}
                    onAdded={() => {
                      setIsAddingItem(false)
                      refetchCart()
                    }}
                    onCancel={() => setIsAddingItem(false)}
                  />
                ) : (
                  <Button type="button" variant="secondary" size="sm" onClick={() => setIsAddingItem(true)}>
                    <PlusIcon />
                    Add an item
                  </Button>
                )}

                <div className="flex flex-col gap-1.5">
                  {cart?.discount_code ? (
                    <p className="text-muted-foreground text-sm">
                      Applied discount code: <span className="text-foreground font-medium">{cart.discount_code}</span>
                    </p>
                  ) : null}
                  <div className="flex items-end gap-2">
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label htmlFor="cart-discount-code">Discount code</Label>
                      <Input
                        id="cart-discount-code"
                        value={discountCode}
                        onChange={(event) => setDiscountCode(event.target.value)}
                      />
                    </div>
                    <Button type="button" variant="secondary" disabled={isSaving || !discountCode} onClick={applyDiscountCode}>
                      Apply
                    </Button>
                    {cart?.discount_code ? (
                      <Button type="button" variant="ghost" disabled={isSaving} onClick={removeDiscountCode}>
                        Remove code
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="cart-phone">Contact phone number</Label>
                  <Input id="cart-phone" value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="cart-email">Email</Label>
                  <Input id="cart-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
                </div>
              </>
            ) : null}
          </div>
          <SheetFooter>
            {cart?.reference_url ? (
              <Button type="button" variant="outline" onClick={copyLink}>
                Copy link
              </Button>
            ) : null}
            <Button
              type="button"
              disabled={!canManageOrder || items.length === 0 || !deliveryRequirementSatisfied || isSaving}
              onClick={markForPaymentAndSendLink}
            >
              {isFreeCart ? 'Mark as paid' : 'Mark for payment and send a link'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      <Confirm
        isOpen={Boolean(deleteItemId)}
        loading={isSaving}
        title="Remove this item from the cart?"
        content="This cannot be undone."
        onConfirm={deleteItem}
        onClose={() => setDeleteItemId(null)}
      />
    </>
  )
}
